import { inject, injectable } from "tsyringe";
import { makeAutoObservable } from "mobx";
import type { ShareLink } from "@/ProjectWorld";
import { TOKEN_IWrapTranslations, type IWrapTranslations } from "@/services/WrapTranslations";
import RunningActionStore, { TOKEN_RunningActionStore } from "../utility-stores/RunningActionStore";
import { AccessLevel } from "@utilitydelta/eventplanedb-client";
import { TOKEN_IProjectEventGenerator, type IProjectEventGenerator } from "@/services/ProjectEventGenerator";

@injectable()
export default class ShareLinkStore {
    private _id: string = '';
    private _createdByMe: boolean = false;
    private _accessLevel?: AccessLevel;
    private _usageCount: number = 0;
    private _createdOn: number = 0;
    public get createdOn(): number {
        return this._createdOn;
    }
    public get usageCount(): number {
        return this._usageCount;
    }
    public set usageCount(value: number) {
        this._usageCount = value;
    }
    private _isActive: boolean = true;
    private _projectId: string | undefined = undefined;
    public get isActive(): boolean {
        return this._isActive;
    }
    public set isActive(value: boolean) {
        this._isActive = value;
    }

    constructor(
        @inject(TOKEN_IProjectEventGenerator) private projectEventGenerator: IProjectEventGenerator,
        @inject(TOKEN_IWrapTranslations) private t: IWrapTranslations,
        @inject(TOKEN_RunningActionStore) private runningActionStore: RunningActionStore,
    ) {
        makeAutoObservable(this);
    }

    init(shareLink: ShareLink, currentUserId: string) {
        this._id = shareLink.id;
        this._createdByMe = shareLink.createdByClientId === currentUserId || shareLink.createdByUserId === currentUserId;
        this._accessLevel = shareLink.accessLevel;
        this._isActive = shareLink.isActive;
        this._projectId = shareLink.pi;
        this._usageCount = shareLink.usageCount ?? 0;
        this._createdOn = shareLink.createdOn;
    }

    get id(): string {
        return this._id;
    }

    get isCreatedByMe(): boolean {
        return this._createdByMe;
    }

    get accessLevelDisplay(): string {
        return this._accessLevel !== undefined ? this.t.t(`access_grants.${this._accessLevel}`) : this.t.t("no_access");
    }

    get usageDisplay(): string {
        return this._usageCount === 1 ? this.t.t('usage_once') : this._usageCount > 0 ? this.t.t('usage_x_times', { count: this._usageCount }) : this.t.t('never_used');
    }


    disable = async () => {
        await this.runningActionStore.manageAction(async () => {
            await this.projectEventGenerator.disableShareLink(this._projectId!, this._id);
        }, true, this.t.t('disabling_share_link'));
    }
}